import { acceptHMRUpdate, defineStore } from 'pinia';
import { http as axios } from '@/services/http.service';
import { subscribeToAppointments } from '@/services/action_cable';

export interface AppointmentDTO {
  id: number;
  professional_id: number;
  professional_name?: string | null;
  requester_id: number;
  requester_email?: string | null;
  datetime: string;
  status?: string | null;
  requester_name?: string | null;
  requester_country?: string | null;
  requester_phone?: string | null;
  specialty_id?: number | null;
  created_at?: string | null;
  session_report?: SessionReportDTO | null;
}

export interface SessionReportDTO {
  id?: number;
  session_summary: string;
  post_session_instructions: string;
}

interface AppointmentsState {
  items: AppointmentDTO[];
  loading: boolean;
  wsInitialized: boolean;
}

// Apex brand-scoped base path when a tenant is given; otherwise the unqualified
// path the brand's own host already resolves against the active schema.
const appointmentsBase = (tenantId?: number | null) =>
  tenantId ? `/api/v1/tenants/${tenantId}/appointments` : '/api/v1/appointments';

export const useAppointmentsStore = defineStore('appointments.store', {
  state: (): AppointmentsState => ({
    items: [],
    loading: false,
    wsInitialized: false,
  }),
  actions: {
    initWebsocket() {
      if (this.wsInitialized) return;
      this.wsInitialized = true;
      subscribeToAppointments<{ event: string; appointment: AppointmentDTO } | unknown>({
        received: (payload) => {
          const p = payload as { event?: string; appointment?: AppointmentDTO };
          if (p && p.event === 'created' && p.appointment) {
            this.addOrUpdate(p.appointment);
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('appointments:created', { detail: p.appointment }),
              );
            }
          }
        },
      });
    },

    addOrUpdate(a: AppointmentDTO) {
      const idx = this.items.findIndex((i) => i.id === a.id);
      if (idx === -1) this.items.unshift(a);
      else this.items[idx] = a;
    },

    async fetchList(
      params?: {
        professional_id?: number;
        requester_id?: number;
        from?: string;
        to?: string;
      },
      tenantId?: number | null,
    ) {
      this.loading = true;
      try {
        const { data } = await axios.get<AppointmentDTO[]>(appointmentsBase(tenantId), {
          params,
        });
        // Deduplicate items by ID to avoid frontend key collisions
        const uniqueItems = Array.isArray(data)
          ? Array.from(new Map(data.map((item) => [item.id, item])).values())
          : [];
        this.items = uniqueItems;
        return this.items;
      } finally {
        this.loading = false;
      }
    },

    async createOne(
      payload: {
        professional_id: number;
        date_time: string;
        requester_email: string;
        requester_name?: string;
        requester_phone?: string;
        time_zone?: string;
        requester_country?: string;
        specialty_id?: number;
      },
      tenantId?: number | null,
    ) {
      const { data } = await axios.post(appointmentsBase(tenantId), { appointment: payload });
      // Optimistically add; real broadcast will also arrive
      this.addOrUpdate(data as AppointmentDTO);
      return data as AppointmentDTO;
    },

    async updateOne(
      id: number,
      payload: {
        status?: string;
        date_time?: string;
        time_zone?: string;
        requester_name?: string;
        requester_country?: string;
        requester_phone?: string;
        session_report?: {
          session_summary: string;
          post_session_instructions: string;
        };
      },
      tenantId?: number | null,
    ) {
      const { data } = await axios.put(`${appointmentsBase(tenantId)}/${id}`, {
        appointment: payload,
      });
      // Update item locally
      const idx = this.items.findIndex((a) => a.id === id);
      if (idx !== -1) this.items[idx] = data as AppointmentDTO;
      return data as AppointmentDTO;
    },

    async deleteOne(id: number, password?: string, tenantId?: number | null) {
      await axios.delete(`${appointmentsBase(tenantId)}/${id}`, { data: { password } });
      this.items = this.items.filter((a) => a.id !== id);
    },
  },
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
if (import.meta.hot) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  import.meta.hot.accept(acceptHMRUpdate(useAppointmentsStore, import.meta.hot));
}
